import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

import { useListParams } from "hooks/useListParams"
import {
  useServiceInvoices,
  useDeleteServiceInvoice,
} from "hooks/useServiceInvoice"
import { formatReadableDate } from "utils/formatDate"

import ServiceInvoiceAddEdit from "./ServiceInvoiceAddEdit"
import ServiceInvoiceView from "./ServiceInvoiceView"
import { ServiceInvoiceHeader } from "./ServiceInvoiceHeader"

const ServiceInvoiceList = () => {
  document.title = "Service Invoices"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewId, setViewId] = useState(null)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useServiceInvoices(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteServiceInvoice()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Service invoice deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete service invoice.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Invoice No",
        accessorKey: "invoice_no",
        cell: ({ row }) => (
          <div className="fw-semibold">{row.original.invoice_no || "-"}</div>
        ),
      },
      {
        header: "Customer",
        accessorKey: "customer.name",
        cell: ({ row }) => row.original.customer?.name || "-",
      },
      {
        header: "Vehicle",
        cell: ({ row }) =>
          row.original.vehicle?.stock_id ||
          row.original.vehicle_info ||
          "-",
      },
      {
        header: "Date",
        accessorKey: "invoice_date",
        cell: ({ row }) =>
          row.original.invoice_date
            ? formatReadableDate(row.original.invoice_date)
            : "-",
      },
      {
        header: "Items",
        cell: ({ row }) => row.original.items?.length ?? 0,
      },
      {
        header: "Total",
        accessorKey: "total_amount",
        cell: ({ row }) => (
          <span className="fw-semibold">
            {Number(row.original.total_amount || 0).toLocaleString()}
          </span>
        ),
      },
      {
        header: "Due",
        accessorKey: "current_due",
        cell: ({ row }) => {
          const due = Number(row.original.current_due || 0)
          return (
            <Badge className={`badge-soft-${due > 0 ? "danger" : "success"}`}>
              {due.toLocaleString()}
            </Badge>
          )
        },
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex gap-1">
              <Button
                color="primary"
                size="sm"
                onClick={() => setViewId(data.id)}
                id={`view-${data.id}`}
              >
                <i className="mdi mdi-eye" />
              </Button>
              <UncontrolledTooltip target={`view-${data.id}`}>
                View
              </UncontrolledTooltip>

              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(data)}
                id={`edit-${data.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${data.id}`}>
                Edit
              </UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(data)}
                id={`delete-${data.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${data.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Service Invoices" breadcrumbItem="Service Invoices">
      <ServiceInvoiceHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      <ServiceInvoiceAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
        onSuccess={refetch}
      />

      <ServiceInvoiceView
        invoiceId={viewId}
        isOpen={Boolean(viewId)}
        toggle={() => setViewId(null)}
        onChanged={refetch}
      />
    </PageWrapper>
  )
}

export default ServiceInvoiceList
